import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input, Button, Alert, Table } from 'reactstrap';
import { addData, updateData, getIdData } from '../../lib/collegerank';
import { getDataWithTypeForSelect } from '../../lib/parameters';

const CollegeModal = ({ isOpen, toggle, editingCollege, onSave, isDarkMode }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [collegeName, setCollegeName] = useState('');
    const [parameters, setParameters] = useState([]);
    const [ranks, setRanks] = useState({});

    const isEdit = !!editingCollege;

    useEffect(() => {
        if (isOpen) {
            fetchParameterData();
            if (isEdit) {
                fetchCollegeData();
            } else {
                setCollegeName('');
                setRanks({});
            }
            setError(null);
        }
    }, [isOpen, editingCollege]);

    const fetchParameterData = async () => {
        try {
            const parameterData = await getDataWithTypeForSelect('DESIRED_COLLEGES');
            setParameters(parameterData);
        } catch (err) {
            console.error('Error fetching parameters:', err);
            setError('Failed to load parameters');
        }
    };

    const fetchCollegeData = async () => {
        try {
            setLoading(true);
            const college = await getIdData(editingCollege.id);
            if (college && college.length > 0) {
                setCollegeName(college[0].collegename);
                const rankValue = college[0].rankvalue ? JSON.parse(college[0].rankvalue) : {};
                setRanks(rankValue);
            }
        } catch (err) {
            console.error('Error fetching college data:', err);
            setError('Failed to load college data');
        } finally {
            setLoading(false);
        }
    };

    const getRank = (key) => {
        return ranks[key] || 0;
    };

    const handleRankChange = (key, value) => {
        setRanks((prev) => ({
            ...prev,
            [key]: parseInt(value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (isEdit) {
                await updateData({
                    id: editingCollege.id,
                    collegename: collegeName,
                    rankvalue: ranks
                });
            } else {
                await addData({
                    collegename: collegeName,
                    rankvalue: ranks
                });
            }

            if (onSave) await onSave();
            toggle();
        } catch (err) {
            console.error('Error saving college:', err);
            setError('Failed to save college');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className={isDarkMode ? 'dark-modal' : ''}>
            <ModalHeader toggle={toggle}>
                {isEdit ? 'Update College' : 'Add College'}
            </ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="college-form">
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">College Name</Label>
                                <Input
                                    type="text"
                                    value={collegeName}
                                    onChange={(e) => setCollegeName(e.target.value)}
                                    placeholder="Enter College Name"
                                    required
                                    disabled={loading}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {parameters.length > 0 && (
                        <Row className="mt-4">
                            <Col sm="12">
                                <Table hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Rank</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parameters.map((param) => (
                                            <tr key={param.value}>
                                                <td>{param.label}</td>
                                                <td>
                                                    <Input
                                                        type="select"
                                                        value={getRank(param.value)}
                                                        onChange={(e) => handleRankChange(param.value, e.target.value)}
                                                        disabled={loading}
                                                        className="form-control"
                                                    >
                                                        <option value={0}>0</option>
                                                        <option value={1}>1</option>
                                                        <option value={2}>2</option>
                                                        <option value={3}>3</option>
                                                    </Input>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    )}
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="college-form" disabled={loading}>
                    {loading ? 'Saving...' : isEdit ? 'Update College' : 'Add College'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CollegeModal;
